import { ChangeEvent, FocusEvent, useEffect, useRef } from 'react'
import Inputmask from 'inputmask'
import cn from 'classnames'

import { useScrollToOnFocus } from 'utils'

export interface InputProps
  extends Omit<React.HTMLProps<HTMLInputElement>, 'onChange' | 'onInput'> {
  onChange?: (value: any) => void
  onInput?: (value: any) => void
  mask?: string
  scrollToOnFocus?: boolean
  selectOnFocus?: boolean
  selectOnRender?: boolean
}

export const Input = (props: InputProps) => {
  const {
    value,
    onInput,
    onChange,
    onFocus,
    onBlur,
    className,
    mask,
    scrollToOnFocus,
    selectOnFocus,
    selectOnRender,
    ...rest
  } = props

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value)
  const onInputHandler = (e: ChangeEvent<HTMLInputElement>) => onInput?.(e.target.value)

  const ref = useRef<HTMLInputElement>(null)
  const focusedOnRender = useRef(false)

  const { onFocus: onHookFocus, onBlur: onHookBlur } = useScrollToOnFocus(ref, scrollToOnFocus)

  const onFocusHandler = (e: FocusEvent<HTMLInputElement>) => {
    onHookFocus()

    if (selectOnFocus) {
      ref.current?.select()
    }

    onFocus?.(e)
  }
  const onBlurHandler = (e: FocusEvent<HTMLInputElement>) => {
    onHookBlur()
    onBlur?.(e)
  }

  useEffect(() => {
    if (selectOnRender && value && !focusedOnRender.current) {
      ref.current?.focus()
      ref.current?.select()
      focusedOnRender.current = true
    }
  }, [selectOnRender, value])

  useEffect(() => {
    if (ref.current && mask) {
      Inputmask({ mask }).mask(ref.current)
    }
  }, [mask])

  return (
    <input
      {...rest}
      ref={ref}
      value={value}
      className={cn('input', className)}
      onInput={onInputHandler}
      onChange={onChangeHandler}
      onFocus={onFocusHandler}
      onBlur={onBlurHandler}
    />
  )
}
